const RAW_MINIO_PUBLIC_BASE = process.env.NEXT_PUBLIC_MINIO_URL?.replace(/\/+$/, '') || '';
// NEXT_PUBLIC_API_URL is the relative proxy base ("/api"), never a site origin, so it must not
// be used here — doing so produced a bogus "/api/minio" media base.
const SITE_ORIGIN = (process.env.NEXT_PUBLIC_SITE_URL || '').replace(/\/+$/, '');
const MINIO_INTERNAL_ORIGINS = [
  process.env.MINIO_INTERNAL_URL,
  'http://minio:9000',
  'http://minio:9002',
  'http://minio',
]
  .flatMap((origin) => (origin ? [origin.replace(/\/+$/, '')] : []));

const MINIO_PUBLIC_BASE = (() => {
  if (!RAW_MINIO_PUBLIC_BASE) return '';

  if (RAW_MINIO_PUBLIC_BASE.startsWith('http://') || RAW_MINIO_PUBLIC_BASE.startsWith('https://')) {
    return RAW_MINIO_PUBLIC_BASE;
  }

  return SITE_ORIGIN ? `${SITE_ORIGIN}${normalizeSlashes(RAW_MINIO_PUBLIC_BASE)}` : RAW_MINIO_PUBLIC_BASE;
})();

const MEDIA_FIELD_NAMES = new Set([
  'attachment',
  'background_image',
  'file',
  'file_path',
  'fullSrc',
  'grow_image',
  'image',
  'image_url',
  'logo_path',
  'media_url',
  'pdf_file',
  'photo',
  'poster',
  'purpose_image',
  'src',
  'thumbSrc',
  'thumbnail',
  'thumbnail_path',
  'thumbnail_url',
  'video',
  'video_mobile',
]);

const MEDIA_FIELD_SUFFIXES = [
  '_file',
  '_image',
  '_media',
  '_path',
  '_photo',
  '_thumbnail',
];

function normalizeSlashes(path: string) {
  return path.startsWith('/') ? path : `/${path}`;
}

function stripInternalMinioOrigin(value: string) {
  for (const origin of MINIO_INTERNAL_ORIGINS) {
    if (value === origin) return '';
    if (value.startsWith(`${origin}/`)) {
      return value.slice(origin.length);
    }
  }

  return undefined;
}

function isPublicAssetPath(value: string) {
  return (
    value.startsWith('/images/') ||
    value.startsWith('/fonts/') ||
    value.startsWith('/icons/') ||
    value.startsWith('/_next/') ||
    value.startsWith('/favicon') ||
    value.startsWith('/download.svg') ||
    value.startsWith('/no-result.svg')
  );
}

function isMediaField(key: string) {
  if (key.endsWith('_alt') || key.endsWith('_type') || key.endsWith('_size')) {
    return false;
  }

  return MEDIA_FIELD_NAMES.has(key) || MEDIA_FIELD_SUFFIXES.some((suffix) => key.endsWith(suffix));
}

export function getPublicMinioUrl(value: string | null | undefined) {
  if (!value || !MINIO_PUBLIC_BASE) return value ?? undefined;

  const internalMinioPath = stripInternalMinioOrigin(value);
  if (internalMinioPath !== undefined) {
    return `${MINIO_PUBLIC_BASE}${normalizeSlashes(internalMinioPath)}`;
  }

  if (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:') ||
    value.startsWith('blob:') ||
    isPublicAssetPath(value) ||
    value === MINIO_PUBLIC_BASE ||
    value.startsWith(`${MINIO_PUBLIC_BASE}/`) ||
    value === RAW_MINIO_PUBLIC_BASE ||
    value.startsWith(`${RAW_MINIO_PUBLIC_BASE}/`)
  ) {
    return value.startsWith(RAW_MINIO_PUBLIC_BASE) && RAW_MINIO_PUBLIC_BASE !== MINIO_PUBLIC_BASE
      ? `${MINIO_PUBLIC_BASE}${value.slice(RAW_MINIO_PUBLIC_BASE.length)}`
      : value;
  }

  return `${MINIO_PUBLIC_BASE}${normalizeSlashes(value)}`;
}

// The Instagram feed backend returns media on its own origin
// (e.g. `https://backend:8080/api/instagram-posts/{id}/media` in production, or the
// backend's public host in dev), which the browser cannot resolve from this app's
// origin and which the production WAF blocks. Rewrite those to the relative proxy
// path so they load through this app's catch-all `/api/[...path]` route. Native
// Instagram CDN URLs (`https://instagram.*`, `https://scontent.*`) are already
// browser-reachable and are returned untouched.
const INSTAGRAM_MEDIA_PATH = /^https?:\/\/[^/]+\/api\/instagram-posts\/[^/]+\/media(?:\/thumbnail)?$/i;

export function normalizeInstagramMediaUrl(value: string | null | undefined) {
  if (!value) return value ?? undefined;

  const match = value.match(INSTAGRAM_MEDIA_PATH);
  if (!match) return value;

  return value.replace(/^https?:\/\/[^/]+/i, '');
}

export function normalizeMediaUrls<T>(value: T, parentKey?: string): T {
  if (Array.isArray(value)) {
    return value.map((item) => normalizeMediaUrls(item, parentKey)) as T;
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => {
      if (typeof entry === 'string' && stripInternalMinioOrigin(entry) !== undefined) {
        return [key, getPublicMinioUrl(entry)];
      }

      if (typeof entry === 'string' && (isMediaField(key) || (key === 'url' && parentKey && isMediaField(parentKey)))) {
        return [key, getPublicMinioUrl(entry)];
      }

      return [key, normalizeMediaUrls(entry, key)];
    })
  ) as T;
}
